import { useEffect, useState } from "react";
import getCustomQuoteDetailsModuleAssetUrl from "mocks/worker/getCustomQuoteDetailsModuleAssetUrl";
import { models } from "../types/api/viewModels";

interface PDFData {
	blob: Blob | null;
	loading: boolean;
	error: string | null;
}

const useCustomQuoteDetailsPDFGenerator = (
	customQuote: models["CustomQuoteViewModel"] | undefined,
	billTo: models["BillToViewModel"] | undefined
) => {
	const [pdfData, setPdfData] = useState<PDFData>({ blob: null, loading: false, error: null });

	const generateCustomQuotePDF = () => {
		if (!customQuote) return;
		const worker = getCustomQuoteDetailsModuleAssetUrl();
		setPdfData({ blob: null, loading: true, error: null });

		worker.postMessage({ customQuote, billTo });

		worker.onmessage = (e: MessageEvent<{ blob?: Blob; error?: string }>) => {
			if (e.data.blob) {
				setPdfData({ blob: e.data.blob, loading: false, error: null });
			} else if (e.data.error) {
				setPdfData({ blob: null, loading: false, error: e.data.error });
			}
			worker.terminate();
		};

		worker.onerror = (error: ErrorEvent) => {
			setPdfData({
				blob: null,
				loading: false,
				error: error.message || "An unknown error occurred"
			});
			worker.terminate();
		};
	};

	const getFormattedFileName = (itemName: string) => {
		const now = new Date();
		const dateStr = now.toLocaleDateString().replace(/\//g, "-");
		const timeStr = now
			.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })
			.replace(/:/g, "_")
			.replace(/\s/g, "");
		return `CustomQuote_${itemName}_${dateStr} at ${timeStr}.pdf`;
	};

	useEffect(() => {
		if (pdfData.blob && customQuote && customQuote.requestName) {
			const fileName = getFormattedFileName(customQuote.requestName);
			const url = window.URL.createObjectURL(pdfData.blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", fileName);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
			setPdfData({ blob: null, loading: false, error: null });
		}
	}, [pdfData.blob, customQuote]);

	return { pdfData, generateCustomQuotePDF };
};

export default useCustomQuoteDetailsPDFGenerator;
